import type { HighlighterCore, LanguageRegistration, ThemedToken } from "shiki/core";

type LangModule = { default: LanguageRegistration[] };

// Curated so the app ships ~45 grammar chunks instead of shiki's 300+;
// each loads only when a message first shows that language.
const LANGS: Record<string, () => Promise<LangModule>> = {
  bash: () => import("shiki/langs/bash.mjs"),
  c: () => import("shiki/langs/c.mjs"),
  cpp: () => import("shiki/langs/cpp.mjs"),
  csharp: () => import("shiki/langs/csharp.mjs"),
  css: () => import("shiki/langs/css.mjs"),
  dart: () => import("shiki/langs/dart.mjs"),
  diff: () => import("shiki/langs/diff.mjs"),
  dockerfile: () => import("shiki/langs/dockerfile.mjs"),
  elixir: () => import("shiki/langs/elixir.mjs"),
  go: () => import("shiki/langs/go.mjs"),
  graphql: () => import("shiki/langs/graphql.mjs"),
  hcl: () => import("shiki/langs/hcl.mjs"),
  html: () => import("shiki/langs/html.mjs"),
  ini: () => import("shiki/langs/ini.mjs"),
  java: () => import("shiki/langs/java.mjs"),
  javascript: () => import("shiki/langs/javascript.mjs"),
  json: () => import("shiki/langs/json.mjs"),
  jsonc: () => import("shiki/langs/jsonc.mjs"),
  jsx: () => import("shiki/langs/jsx.mjs"),
  kotlin: () => import("shiki/langs/kotlin.mjs"),
  lua: () => import("shiki/langs/lua.mjs"),
  makefile: () => import("shiki/langs/makefile.mjs"),
  markdown: () => import("shiki/langs/markdown.mjs"),
  nix: () => import("shiki/langs/nix.mjs"),
  php: () => import("shiki/langs/php.mjs"),
  powershell: () => import("shiki/langs/powershell.mjs"),
  proto: () => import("shiki/langs/proto.mjs"),
  python: () => import("shiki/langs/python.mjs"),
  r: () => import("shiki/langs/r.mjs"),
  ruby: () => import("shiki/langs/ruby.mjs"),
  rust: () => import("shiki/langs/rust.mjs"),
  scss: () => import("shiki/langs/scss.mjs"),
  sql: () => import("shiki/langs/sql.mjs"),
  svelte: () => import("shiki/langs/svelte.mjs"),
  swift: () => import("shiki/langs/swift.mjs"),
  terraform: () => import("shiki/langs/terraform.mjs"),
  toml: () => import("shiki/langs/toml.mjs"),
  tsx: () => import("shiki/langs/tsx.mjs"),
  typescript: () => import("shiki/langs/typescript.mjs"),
  vue: () => import("shiki/langs/vue.mjs"),
  xml: () => import("shiki/langs/xml.mjs"),
  yaml: () => import("shiki/langs/yaml.mjs"),
};

const ALIASES: Record<string, string> = {
  "c++": "cpp",
  "c#": "csharp",
  cs: "csharp",
  console: "bash",
  docker: "dockerfile",
  golang: "go",
  js: "javascript",
  kt: "kotlin",
  md: "markdown",
  mjs: "javascript",
  cjs: "javascript",
  protobuf: "proto",
  ps1: "powershell",
  py: "python",
  rb: "ruby",
  rs: "rust",
  sh: "bash",
  shell: "bash",
  shellscript: "bash",
  tf: "terraform",
  ts: "typescript",
  yml: "yaml",
  zsh: "bash",
};

/** Canonical grammar id for a fence tag, or null when unsupported. */
export function resolveLanguage(tag: string | null | undefined): string | null {
  const key = (tag ?? "").trim().toLowerCase();
  if (!key) return null;
  const id = ALIASES[key] ?? key;
  return id in LANGS ? id : null;
}

let highlighter: Promise<HighlighterCore> | null = null;
const loaded = new Map<string, Promise<void>>();

function getHighlighter(): Promise<HighlighterCore> {
  highlighter ??= (async () => {
    const [{ createHighlighterCore, createCssVariablesTheme }, { createJavaScriptRegexEngine }] =
      await Promise.all([import("shiki/core"), import("shiki/engine/javascript")]);
    return createHighlighterCore({
      themes: [createCssVariablesTheme({ name: "nurion", variablePrefix: "--chat-code-" })],
      langs: [],
      engine: createJavaScriptRegexEngine(),
    });
  })();
  return highlighter;
}

/** Tokenise `code` as `lang` (a `resolveLanguage` id). Colours are CSS variables. */
export async function highlight(code: string, lang: string): Promise<ThemedToken[][]> {
  const core = await getHighlighter();
  let ready = loaded.get(lang);
  if (!ready) {
    const load = LANGS[lang];
    if (!load) throw new Error(`unsupported language: ${lang}`);
    ready = load().then((mod) => core.loadLanguage(...mod.default));
    loaded.set(lang, ready);
  }
  await ready;
  // Shiki's 500 ms per-line budget paints the rest of a line in one colour
  // while a grammar's regexes first compile on a slow device; allow more.
  return core.codeToTokensBase(code, {
    lang,
    theme: "nurion",
    tokenizeTimeLimit: 5000,
    tokenizeMaxLineLength: 2000,
  });
}
